import { tzOpts } from "@/lib/tz";
import { Check, CheckCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { MediaView } from "@/components/MediaView";
import { NewConversationDialog } from "@/components/NewConversationDialog";
import { NotesPanel, playBeep, soundEnabled, setSoundEnabled, useTitleBlink, fmtWait } from "@/components/ChatExtras";
import { PauseCircle, StopCircle, TimerReset, RefreshCw, MessageCircle, Send, FileText, CalendarClock, CheckCircle2, XCircle, AlertTriangle, Wallet, User, Loader2, Wifi, WifiOff, Zap, Paperclip, X, MessageCirclePlus, Bell, BellOff, Eye, RotateCcw, WifiOff as WifiOffIcon, StickyNote } from "lucide-react";

const TERMINATE_CAUSE = {
  "User-Request": "desconexão solicitada pelo roteador/cliente",
  "Lost-Carrier": "sinal perdido (queda de conexão)",
  "Lost-Service": "serviço interrompido",
  "Idle-Timeout": "tempo ocioso esgotado",
  "Session-Timeout": "tempo máximo de sessão atingido",
  "Admin-Reset": "reiniciado pelo administrador da rede",
  "Admin-Reboot": "equipamento reiniciado",
  "NAS-Reboot": "concentrador (NAS) reiniciado",
  "NAS-Error": "erro no concentrador (NAS)",
};

function fmtDuration(fromIso) {
  if (!fromIso) return "—";
  const ms = Date.now() - new Date(fromIso.endsWith("Z") || fromIso.includes("+") ? fromIso : fromIso + "Z").getTime();
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

const fmtRemaining = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const fmtMoney = (v) => `R$ ${(Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : "—");

const REASON = { handoff: "Cliente pediu atendente", attendant: "Pausado pelo atendente (#0)", outbound: "Iniciado por nós", loop: "Loop de robôs detectado" };

function ClientSidebar({ phone, refreshKey, onInvoiceSent }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [when, setWhen] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [otherQ, setOtherQ] = useState("");
  const [otherRows, setOtherRows] = useState(null); // null = ainda nao buscou
  const [otherBusy, setOtherBusy] = useState("");

  const searchOther = async () => {
    if (!otherQ.trim()) return;
    setOtherBusy("search");
    try {
      const { data } = await api.post(`/chat-sessions/${phone}/other-client/search`, { query: otherQ });
      setOtherRows(data.items || []);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setOtherBusy("");
    }
  };

  const sendOther = async (row) => {
    if (!window.confirm(`Enviar a fatura de ${row.nome} (${row.login}) para esta conversa?`)) return;
    setOtherBusy(row.login);
    try {
      await api.post(`/chat-sessions/${phone}/send-invoice`, { login: row.login });
      toast.success("Fatura enviada nesta conversa");
      setOtherRows(null); setOtherQ("");
      onInvoiceSent?.();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setOtherBusy("");
    }
  };

  const load = useCallback(async () => {
    if (!phone) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/chat-sessions/${phone}/client-summary`);
      setSummary(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  }, [phone]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const sendInvoice = async () => {
    setSendingInvoice(true);
    try {
      await api.post(`/chat-sessions/${phone}/send-invoice`);
      toast.success("Fatura enviada ao cliente");
      onInvoiceSent?.();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSendingInvoice(false);
    }
  };

  const reschedule = async () => {
    if (!when) {
      toast.error("Escolha o dia e a hora");
      return;
    }
    setScheduling(true);
    try {
      await api.post(`/chat-sessions/${phone}/reschedule`, { when });
      toast.success("Pagamento reagendado");
      setWhen("");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setScheduling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carregando…
      </div>
    );
  }

  if (!summary?.identified) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4 gap-2">
        <User className="w-6 h-6 text-slate-600" />
        <p className="text-slate-500 text-sm">Este número não corresponde a nenhum cliente cadastrado.</p>
      </div>
    );
  }

  const inv = summary.open_invoice;
  const overdue = inv && (inv.dias_atraso ?? 0) > 0;

  return (
    <div className="h-full overflow-y-auto space-y-4 pr-1 themed-scrollbar" data-testid="client-sidebar">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Cliente</div>
        <div className="text-white font-medium mt-0.5">{summary.nome}</div>
        <div className="text-xs font-mono-jb text-slate-500">{summary.login}</div>
        {summary.cliente_desde && (
          <div className="text-[11px] text-slate-600 mt-0.5">Cliente desde {fmtDate(summary.cliente_desde)}</div>
        )}
        <div className="flex gap-1.5 mt-2">
          {summary.status_ativo ? (
            <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px]">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Ativo
            </Badge>
          ) : (
            <Badge className="bg-slate-700 text-slate-300 border border-slate-600 text-[10px]">Inativo</Badge>
          )}
          {summary.bloqueado && (
            <Badge className="bg-red-500/15 text-red-300 border border-red-500/30 text-[10px]">
              <XCircle className="w-3 h-3 mr-1" /> Bloqueado
            </Badge>
          )}
        </div>
      </div>

      <div className="border-t border-slate-800 pt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">Conexão (internet)</div>
        {summary.conexao?.conectado ? (
          <div className="rounded-md p-2.5 border bg-emerald-500/5 border-emerald-500/30">
            <div className="flex items-center gap-1.5 text-emerald-300 text-sm">
              <Wifi className="w-3.5 h-3.5" /> Conectado há {fmtDuration(summary.conexao.desde)}
            </div>
            {summary.conexao.ip && (
              <div className="text-[11px] text-slate-500 mt-0.5 font-mono-jb">IP {summary.conexao.ip}</div>
            )}
          </div>
        ) : summary.conexao?.ultima_desconexao ? (
          <div className="rounded-md p-2.5 border bg-slate-800/50 border-slate-700">
            <div className="flex items-center gap-1.5 text-slate-300 text-sm">
              <WifiOff className="w-3.5 h-3.5 text-slate-500" /> Desconectado há {fmtDuration(summary.conexao.ultima_desconexao)}
            </div>
            {summary.conexao.motivo_desconexao && (
              <div className="text-[11px] text-slate-500 mt-0.5">
                {TERMINATE_CAUSE[summary.conexao.motivo_desconexao] || summary.conexao.motivo_desconexao}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-600">Sem registro de conexão.</div>
        )}
      </div>

      <div className="border-t border-slate-800 pt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">Fatura em aberto</div>
        {inv ? (
          <div className={`rounded-md p-2.5 border ${overdue ? "bg-red-500/5 border-red-500/30" : "bg-slate-800/50 border-slate-700"}`}>
            <div className="flex items-center justify-between">
              <span className="text-white font-mono-jb text-sm">{fmtMoney(inv.valor)}</span>
              {overdue && (
                <span className="text-[10px] text-red-300 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {inv.dias_atraso}d atraso
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Vence em {fmtDate(inv.vencimento)}</div>
            <Button
              size="sm" onClick={sendInvoice} disabled={sendingInvoice}
              data-testid="send-invoice-btn"
              className="w-full mt-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs"
            >
              {sendingInvoice ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
              Enviar fatura
            </Button>
          </div>
        ) : (
          <div className="text-xs text-slate-600">Nenhuma fatura em aberto.</div>
        )}
      </div>

      <div className="border-t border-slate-800 pt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">Último pagamento</div>
        {summary.last_payment ? (
          <div className="flex items-center gap-2 text-sm">
            <Wallet className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <div>
              <div className="text-white font-mono-jb">{fmtMoney(summary.last_payment.valor)}</div>
              <div className="text-[11px] text-slate-500">{fmtDate(summary.last_payment.data)}</div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-slate-600">Nenhum pagamento registrado.</div>
        )}
      </div>

      <div className="border-t border-slate-800 pt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 flex items-center gap-1.5">
          <CalendarClock className="w-3.5 h-3.5" /> Reagendar pagamento
        </div>
        {summary.rem_obs && (
          <div className="text-[11px] text-amber-300/80 mb-2">
            Já reagendado para {new Date(summary.rem_obs).toLocaleString("pt-BR", tzOpts())}
          </div>
        )}
        <Label className="text-[10px] text-slate-500">Novo dia e hora</Label>
        <Input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          data-testid="reschedule-input"
          className="bg-slate-900 border-slate-800 text-white text-xs mt-1 h-8"
        />
        <Button
          size="sm" onClick={reschedule} disabled={scheduling}
          data-testid="reschedule-btn"
          variant="outline"
          className="w-full mt-2 border-slate-700 hover:bg-slate-800 hover:text-white text-xs"
        >
          {scheduling ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5 mr-1.5" />}
          Salvar reagendamento
        </Button>
      </div>

      <div className="border-t border-slate-800 pt-3" data-testid="other-invoice">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" /> Fatura de outra pessoa
        </div>
        <Input
          value={otherQ} onChange={(e) => setOtherQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") searchOther(); }}
          placeholder="CPF/CNPJ ou login do titular"
          data-testid="other-invoice-q"
          className="bg-slate-900 border-slate-800 text-white text-xs h-8"
        />
        <Button size="sm" variant="outline" onClick={searchOther} disabled={otherBusy === "search" || !otherQ.trim()}
          className="w-full mt-2 border-slate-700 hover:bg-slate-800 hover:text-white text-xs" data-testid="other-invoice-search">
          {otherBusy === "search" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
          Buscar titular
        </Button>
        {otherRows && otherRows.length === 0 && <div className="text-xs text-slate-500 mt-2">Nenhum cadastro encontrado.</div>}
        {(otherRows || []).map((r) => (
          <div key={r.login} className="mt-2 rounded-md border border-slate-700 bg-slate-800/50 p-2">
            <div className="text-xs text-white">{r.nome}</div>
            <div className="text-[11px] text-slate-500">
              {r.login}{r.bairro ? ` · ${r.bairro}` : ""}{r.cidade ? ` - ${r.cidade}` : ""}
            </div>
            <div className="text-[11px] mt-0.5 text-slate-400">
              {r.open_titles > 0 ? `${r.open_titles} fatura(s) em aberto` : "Sem fatura em aberto"}
            </div>
            <Button size="sm" disabled={r.open_titles === 0 || !!otherBusy} onClick={() => sendOther(r)}
              className="w-full mt-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs"
              data-testid={`other-invoice-send-${r.login}`}>
              {otherBusy === r.login ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
              Enviar esta fatura
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Recibo real do WhatsApp: 1 tique = enviada, 2 cinza = entregue, 2 azuis = lida.
// Sem status (mensagem antiga ou sem ID) nao mostra nada - nada e inventado.
function Ticks({ status }) {
  if (!status) return null;
  if (status === "sent") return <Check className="inline w-3.5 h-3.5 ml-1 text-slate-400" title="Enviada" />;
  if (status === "delivered") return <CheckCheck className="inline w-3.5 h-3.5 ml-1 text-slate-400" title="Entregue" />;
  return <CheckCheck className="inline w-3.5 h-3.5 ml-1 text-sky-400" title="Lida" />;
}

function AssignBar({ phone, assigned, onChanged }) {
  const { user } = useAuth();
  const canTransfer = ["admin", "superadmin"].includes(user?.team_role || "admin") || (user?.perms || []).includes("chat.transfer");
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);

  const call = async (to) => {
    setBusy(true);
    try {
      await api.post(`/chat-sessions/${phone}/assign`, to ? { to } : {});
      toast.success(to ? "Conversa transferida" : "Você assumiu a conversa");
      setOpen(false);
      onChanged?.();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };
  const openTransfer = async () => {
    setOpen((v) => !v);
    if (!members.length) {
      try { const { data } = await api.get("/team/assignable"); setMembers(data.items || []); }
      catch (ex) { toast.error(formatApiError(ex)); }
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-800 bg-slate-900/60 px-3 py-1.5 text-xs" data-testid="assign-bar">
      <User className="w-3.5 h-3.5 text-slate-500" />
      <span className="text-slate-400">Responsável:</span>
      <b className={assigned?.mine ? "text-emerald-300" : "text-white"}>
        {assigned?.assigned_to ? (assigned.mine ? "você" : (assigned.assigned_name || assigned.assigned_to)) : "ninguém ainda"}
      </b>
      {!assigned?.mine && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => call(null)} data-testid="assume-btn"
          className="h-6 px-2 text-[11px] border-slate-700 hover:bg-slate-800">Assumir</Button>
      )}
      {canTransfer && (
        <Button size="sm" variant="outline" disabled={busy} onClick={openTransfer} data-testid="transfer-btn"
          className="h-6 px-2 text-[11px] border-slate-700 hover:bg-slate-800">Transferir…</Button>
      )}
      {open && (
        <select defaultValue="" onChange={(e) => e.target.value && call(e.target.value)} data-testid="transfer-select"
          className="h-6 rounded bg-slate-900 border border-slate-700 text-white text-[11px] px-1">
          <option value="">Escolha quem recebe…</option>
          {members.filter((m) => m.email !== assigned?.assigned_to).map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
      )}
    </div>
  );
}

function ConversationDialog({ phone, clientName, open, onClose, onSent, tabs = [], onSelectTab, onCloseTab, incoming = [], onPull, onDismiss, viewingBy = null, onMeta, assigned = null }) {
  const [messages, setMessages] = useState([]);
  const [protocol, setProtocol] = useState("");
  const [sendingHistory, setSendingHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sidebarRefresh, setSidebarRefresh] = useState(0);
  const [quickReplies, setQuickReplies] = useState([]);
  const [slashIdx, setSlashIdx] = useState(0);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [pending, setPending] = useState(null); // { file, preview }
  const [dragging, setDragging] = useState(false);

  // Varias conversas na mesma janela (abas): ao trocar de aba, limpa a tela da anterior
  // e guarda/restaura o rascunho de cada conversa.
  const draftsRef = useRef({});
  const textRef = useRef("");
  textRef.current = text;
  useEffect(() => {
    setMessages([]);
    setText(draftsRef.current[phone] || "");
    prevLenRef.current = 0;
    nearBottomRef.current = true;
    setPending((cur) => { if (cur?.preview) URL.revokeObjectURL(cur.preview); return null; });
    return () => { draftsRef.current[phone] = textRef.current; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const clearPending = () => {
    setPending((cur) => {
      if (cur?.preview) URL.revokeObjectURL(cur.preview);
      return null;
    });
  };

  // Anexa um arquivo (clipe, Ctrl+V ou arrastar e soltar). Um por envio.
  const attach = (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Arquivo acima de 20 MB — o WhatsApp não aceita pelo painel.");
      return;
    }
    let f = file;
    // Print colado vem sempre como "image.png": dá um nome que identifique.
    if (/^image\.[a-z]+$/i.test(file.name || "") && file.type.startsWith("image/")) {
      const d = new Date();
      const p = (n) => String(n).padStart(2, "0");
      const ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg");
      f = new File([file], `captura-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.${ext}`, { type: file.type });
    }
    setPending((cur) => {
      if (cur?.preview) URL.revokeObjectURL(cur.preview);
      return { file: f, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null };
    });
    textareaRef.current?.focus();
  };

  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return; // texto comum: cola normalmente
    e.preventDefault();
    if (files.length > 1) toast.info("Vários arquivos colados: anexei o primeiro. Envie e cole o próximo.");
    attach(files[0]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) attach(file);
  };

  useEffect(() => {
    if (!open) clearPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Respostas rapidas ja preenchidas com os dados do cliente desta conversa
  // (nome, fatura, saudacao...). Recarrega quando a barra lateral atualiza.
  useEffect(() => {
    if (!open || !phone) return;
    api.get(`/chat-sessions/${phone}/quick-replies`)
      .then(({ data }) => setQuickReplies(data.items || []))
      .catch(() => {});
  }, [open, phone, sidebarRefresh]);

  const insertQuickReply = (qr, replaceAll = false) => {
    if (qr.missing?.length) {
      toast.error(`Sem dados nesta conversa: ${qr.missing.map((m) => `{${m}}`).join(", ")}`);
      return;
    }
    setText((cur) => {
      const trimmed = cur.trimEnd();
      return replaceAll || !trimmed ? qr.text : `${trimmed}\n${qr.text}`;
    });
    textareaRef.current?.focus();
  };

  // Atalho "/": digitar "/" (+ parte do nome) no campo abre a busca de respostas.
  const norm = (v) => (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const slashActive = text.startsWith("/") && !text.includes("\n") && quickReplies.length > 0 && !pending;
  const slashQuery = slashActive ? norm(text.slice(1)) : "";
  const slashMatches = slashActive
    ? quickReplies.filter((q) => norm(q.label).includes(slashQuery) || norm(q.text).includes(slashQuery)).slice(0, 8)
    : [];
  const slashSel = Math.min(slashIdx, Math.max(0, slashMatches.length - 1));
  useEffect(() => { setSlashIdx(0); }, [slashQuery]);

  const load = useCallback(async (silent = false) => {
    if (!phone) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/chat-sessions/${phone}/thread`, { params: { limit: 100 } });
      setMessages(data.messages || []);
      setProtocol(data.protocol || "");
    } catch (ex) {
      if (!silent) toast.error(formatApiError(ex));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [phone]);

  useEffect(() => {
    if (!open || !phone) return;
    load();
    pollRef.current = setInterval(() => load(true), 8000);
    return () => clearInterval(pollRef.current);
  }, [open, phone, load]);

  // So rola pro fim quando chega mensagem NOVA (ou na abertura/envio) e a pessoa ja
  // estava no fim. A atualizacao silenciosa (a cada 8 s, p/ os tiques) nao mexe na
  // posicao de quem esta lendo mensagens antigas.
  const prevLenRef = useRef(0);
  const nearBottomRef = useRef(true);
  useEffect(() => { prevLenRef.current = 0; nearBottomRef.current = true; }, [phone, open]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const first = prevLenRef.current === 0;
    const grew = messages.length > prevLenRef.current;
    prevLenRef.current = messages.length;
    if (first || (grew && nearBottomRef.current)) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Mensagens que falharam ficam na conversa (em vermelho) com "Reenviar", em vez de so um aviso que some.
  const [failed, setFailed] = useState([]);

  const deliver = async (body, file) => {
    if (file) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", body);
      await api.post(`/chat-sessions/${phone}/reply-media`, fd);
    } else {
      await api.post(`/chat-sessions/${phone}/reply`, { text: body });
    }
  };

  const send = async () => {
    const body = text.trim();
    if ((!body && !pending) || sending) return;
    const file = pending?.file || null;
    setSending(true);
    try {
      await deliver(body, file);
      if (file) clearPending();
      setText("");
      nearBottomRef.current = true;
      await load();
      onSent?.();
    } catch (ex) {
      // guarda a mensagem como "nao enviada" e limpa o campo, pra nao perder o texto nem duplicar sem querer
      setFailed((cur) => [...cur, { id: `${Date.now()}-${Math.random()}`, phone, body, file, error: formatApiError(ex), at: new Date().toISOString() }]);
      if (file) clearPending();
      setText("");
      nearBottomRef.current = true;
      toast.error(formatApiError(ex));
    } finally {
      setSending(false);
    }
  };

  const retryFailed = async (f) => {
    if (sending) return;
    setSending(true);
    try {
      await deliver(f.body, f.file);
      setFailed((cur) => cur.filter((x) => x.id !== f.id));
      nearBottomRef.current = true;
      await load();
      onSent?.();
      toast.success("Mensagem enviada");
    } catch (ex) {
      setFailed((cur) => cur.map((x) => (x.id === f.id ? { ...x, error: formatApiError(ex) } : x)));
      toast.error(formatApiError(ex));
    } finally {
      setSending(false);
    }
  };
  const discardFailed = (id) => setFailed((cur) => cur.filter((x) => x.id !== id));
  const failedHere = failed.filter((f) => f.phone === phone);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-4xl flex flex-col h-[75vh]" data-testid="conversation-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-400" />
            {clientName || phone}
            {clientName && <span className="text-xs font-mono-jb text-slate-500">{phone}</span>}
          </DialogTitle>
        </DialogHeader>

        <AssignBar phone={phone} assigned={assigned} onChanged={onMeta} />

        {viewingBy && (
          <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-100" data-testid="viewing-warning">
            <Eye className="w-3.5 h-3.5 shrink-0" />
            <span><b>{viewingBy.split("@")[0]}</b> está com esta conversa aberta agora. Combine antes de responder, para o cliente não receber duas respostas.</span>
          </div>
        )}

        {incoming.length > 0 && (
          <div className="space-y-1.5" data-testid="incoming-chats">
            {incoming.map((c) => (
              <div key={c.phone} className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm">
                <Zap className="w-4 h-4 text-emerald-300 shrink-0" />
                <div className="flex-1 min-w-0 truncate text-emerald-50">
                  {c.needs_reply ? "Cliente respondeu" : "Novo atendimento"}:{" "}
                  <b>{c.client_name || c.phone}</b>
                  {c.client_name && <span className="ml-2 font-mono-jb text-xs text-emerald-200/70">{c.phone}</span>}
                </div>
                <Button size="sm" onClick={() => onPull?.(c.phone)} data-testid={`pull-chat-${c.phone}`}
                  className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 h-7 text-xs">Puxar atendimento</Button>
                <Button size="sm" variant="ghost" onClick={() => onDismiss?.(c.phone)}
                  className="h-7 text-xs text-emerald-200 hover:text-white">Depois</Button>
              </div>
            ))}
          </div>
        )}

        {tabs.length > 1 && (
          <div className="flex gap-1 overflow-x-auto border-b border-slate-800" data-testid="conversation-tabs">
            {tabs.map((tb) => (
              <div key={tb.phone}
                className={`flex items-center gap-1 rounded-t-md border border-b-0 px-3 py-1.5 text-xs cursor-pointer whitespace-nowrap ${
                  tb.phone === phone ? "bg-slate-900 border-slate-700 text-white" : "bg-slate-950 border-slate-800 text-slate-400 hover:text-white"}`}
                onClick={() => onSelectTab?.(tb.phone)}>
                {tb.unread && tb.phone !== phone && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" title="Mensagem nova" />}
                {tb.label}
                <button type="button" aria-label="Fechar aba" className="ml-1 text-slate-500 hover:text-white"
                  onClick={(e) => { e.stopPropagation(); onCloseTab?.(tb.phone); }}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 flex gap-4 min-h-0">
          <div className="flex-1 flex flex-col min-w-0">
            {protocol && (
              <div className="flex items-center gap-2 pb-2 text-xs text-slate-400" data-testid="conversation-protocol">
                <span>Protocolo <b className="font-mono-jb text-slate-200">{protocol}</b></span>
                <button type="button" className="underline hover:text-white" onClick={() => { navigator.clipboard?.writeText(protocol); toast.success("Protocolo copiado"); }}>copiar</button>
                <Button size="sm" variant="outline" className="ml-auto h-7 text-xs border-slate-700 text-slate-200" disabled={sendingHistory}
                  data-testid="send-history-btn"
                  onClick={async () => {
                    setSendingHistory(true);
                    try {
                      const { data: r } = await api.post(`/chat-sessions/${phone}/send-history`);
                      toast.success(`Histórico enviado ao cliente (${r.parts} mensagem${r.parts === 1 ? "" : "s"})`);
                    } catch (ex) { toast.error(formatApiError(ex)); } finally { setSendingHistory(false); }
                  }}>
                  <FileText className="w-3 h-3 mr-1" />{sendingHistory ? "Enviando…" : "Enviar histórico ao cliente"}
                </Button>
              </div>
            )}
            <div
              ref={scrollRef}
              onScroll={(e) => {
                const el = e.currentTarget;
                nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
              }}
              data-testid="conversation-messages"
              className="flex-1 overflow-y-auto space-y-2 py-2 px-1 bg-slate-900/40 rounded-md border border-slate-800 themed-scrollbar"
            >
              {loading && <div className="text-center text-slate-500 text-sm py-6">Carregando…</div>}
              {!loading && messages.length === 0 && (
                <div className="text-center text-slate-500 text-sm py-6">Nenhuma mensagem ainda.</div>
              )}
              {!loading && messages.map((m, i) => (
                <div key={i} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      m.direction === "out"
                        ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-50"
                        : "bg-slate-800 border border-slate-700 text-slate-200"
                    }`}
                  >
                    {m.media && (
                      <div className={m.text ? "mb-1.5" : ""}>
                        <MediaView
                          media={m.media}
                          transcript={m.transcript}
                          transcriptStatus={m.transcript_status}
                          onLoad={() => {
                            if (scrollRef.current && nearBottomRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                          }}
                        />
                      </div>
                    )}
                    {m.text}
                    <div className="text-[10px] text-slate-500 mt-1">
                      {new Date(m.created_at).toLocaleTimeString("pt-BR", tzOpts())}
                      {m.direction === "out" && m.sent_by && <span> · {m.sent_by.split("@")[0]}</span>}
                      {m.direction === "out" && <Ticks status={m.wa_status} />}
                    </div>
                  </div>
                </div>
              ))}
              {!loading && failedHere.map((f) => (
                <div key={f.id} className="flex justify-end" data-testid="failed-message">
                  <div className="max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words bg-red-500/10 border border-red-500/40 text-red-100">
                    {f.file && <div className="text-xs text-red-200/80 mb-1">📎 {f.file.name}</div>}
                    {f.body}
                    <div className="text-[11px] text-red-300 mt-1.5">Não enviada: {f.error}</div>
                    <div className="flex gap-2 mt-1.5">
                      <Button size="sm" onClick={() => retryFailed(f)} disabled={sending}
                        className="h-6 text-[11px] bg-red-500/80 hover:bg-red-400 text-white">
                        <RotateCcw className="w-3 h-3 mr-1" /> Reenviar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => discardFailed(f.id)}
                        className="h-6 text-[11px] text-red-200 hover:text-white">Descartar</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {quickReplies.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-3" data-testid="quick-replies-bar">
                {quickReplies.map((qr, i) => {
                  const blocked = qr.missing?.length > 0;
                  return (
                    <button
                      key={i}
                      type="button"
                      data-testid={`quick-reply-${i}`}
                      onClick={() => insertQuickReply(qr)}
                      title={blocked ? `Sem dados nesta conversa: ${qr.missing.map((m) => `{${m}}`).join(", ")}` : qr.text}
                      className={`flex items-center gap-1 text-[11px] border rounded-full px-2.5 py-1 transition-colors ${
                        blocked
                          ? "bg-slate-900 border-slate-800 text-slate-600 cursor-not-allowed"
                          : "bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-300 hover:text-white"
                      }`}
                    >
                      <Zap className={`w-3 h-3 ${blocked ? "text-slate-700" : "text-emerald-400"}`} /> {qr.label}
                    </button>
                  );
                })}
                <span className="text-[10px] text-slate-600 self-center ml-1">ou digite “/” no campo</span>
              </div>
            )}

            <div
              className={`pt-2 ${dragging ? "ring-2 ring-emerald-500/60 rounded-md" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              data-testid="composer-dropzone"
            >
              {pending && (
                <div className="flex items-center gap-3 mb-2 rounded-md border border-slate-700 bg-slate-900 px-2.5 py-2" data-testid="attachment-preview">
                  {pending.preview ? (
                    <img src={pending.preview} alt="" className="h-14 w-14 rounded object-cover" />
                  ) : (
                    <FileText className="h-8 w-8 text-slate-400 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">{pending.file.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {pending.file.size < 1024 * 1024
                        ? `${Math.max(1, Math.round(pending.file.size / 1024))} KB`
                        : `${(pending.file.size / 1024 / 1024).toFixed(1)} MB`}
                      {" · "}o texto digitado abaixo vai como legenda
                    </div>
                  </div>
                  <button type="button" onClick={clearPending} className="text-slate-400 hover:text-white p-1" title="Remover anexo" data-testid="attachment-remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <input ref={fileInputRef} type="file" className="hidden" data-testid="attach-input"
                       onChange={(e) => { attach(e.target.files?.[0]); e.target.value = ""; }} />
                <Button type="button" variant="ghost" onClick={() => fileInputRef.current?.click()}
                        className="text-slate-400 hover:text-white shrink-0 px-2" title="Anexar arquivo" data-testid="attach-btn">
                  <Paperclip className="w-4 h-4" />
                </Button>
                <div className="relative flex-1 min-w-0">
                {slashActive && slashMatches.length > 0 && (
                  <div className="absolute bottom-full mb-1 left-0 right-0 z-20 rounded-md border border-slate-700 bg-slate-900 shadow-lg overflow-hidden" data-testid="slash-menu">
                    {slashMatches.map((q, i) => (
                      <button
                        key={`${q.label}-${i}`} type="button"
                        onMouseDown={(e) => { e.preventDefault(); insertQuickReply(q, true); }}
                        className={`w-full text-left px-3 py-1.5 text-xs flex gap-2 items-baseline ${
                          i === slashSel ? "bg-slate-800" : ""} ${q.missing?.length ? "text-slate-600" : "text-slate-200"}`}
                      >
                        <span className="font-medium shrink-0"><Zap className="w-3 h-3 inline text-emerald-400 mr-1" />{q.label}</span>
                        <span className="truncate text-slate-500">
                          {q.missing?.length ? `sem dados: ${q.missing.map((m) => `{${m}}`).join(", ")}` : q.text}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <Textarea
                  ref={textareaRef}
                  data-testid="reply-textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onPaste={onPaste}
                  onKeyDown={(e) => {
                    if (slashActive && slashMatches.length > 0) {
                      if (e.key === "ArrowDown") { e.preventDefault(); setSlashIdx((slashSel + 1) % slashMatches.length); return; }
                      if (e.key === "ArrowUp") { e.preventDefault(); setSlashIdx((slashSel - 1 + slashMatches.length) % slashMatches.length); return; }
                      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertQuickReply(slashMatches[slashSel], true); return; }
                      if (e.key === "Escape") { e.preventDefault(); setText(""); return; }
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder={pending ? "Legenda (opcional)… Enter envia" : "Digite sua resposta… (Enter envia, Shift+Enter quebra linha, Ctrl+V cola print ou arquivo)"}
                  rows={2}
                  className="bg-slate-900 border-slate-800 text-white resize-none focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
                />
                </div>
                <Button
                  data-testid="send-reply-btn"
                  onClick={send}
                  disabled={sending || (!text.trim() && !pending)}
                  className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold shrink-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="w-64 shrink-0 border-l border-slate-800 pl-4 overflow-y-auto themed-scrollbar">
            <ClientSidebar
              phone={phone}
              refreshKey={sidebarRefresh}
              onInvoiceSent={() => {
                load();
                setSidebarRefresh((k) => k + 1);
              }}
            />
            <NotesPanel phone={phone} onChanged={onMeta} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PausedSessionsPanel() {
  const [data, setData] = useState({ items: [], pause_minutes: 10 });
  const [busy, setBusy] = useState(null);
  const [openChat, setOpenChat] = useState(null); // aba ativa
  const [tabs, setTabs] = useState([]); // telefones abertos na janela de atendimento
  const [dismissed, setDismissed] = useState({}); // avisos "Depois" (por telefone)
  const [newOpen, setNewOpen] = useState(false);
  const [sound, setSound] = useState(soundEnabled());
  const [seen, setSeen] = useState({}); // ultima mensagem do cliente ja vista, por aba
  const [blink, setBlink] = useState(false);
  const alertedRef = useRef(null); // chaves de aviso ja tocadas (null = primeira carga, nao toca)

  const openTab = (ph) => {
    setTabs((cur) => (cur.includes(ph) ? cur : [...cur, ph]));
    setOpenChat(ph);
  };
  const closeTab = (ph) => {
    setTabs((cur) => {
      const next = cur.filter((x) => x !== ph);
      setOpenChat((active) => (active === ph ? (next[Math.max(0, cur.indexOf(ph) - 1)] ?? next[0] ?? null) : active));
      return next;
    });
  };
  const closeAll = () => { setTabs([]); setOpenChat(null); };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/chat-sessions/paused");
      setData(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  }, []);

  const hasTabs = tabs.length > 0;
  useEffect(() => {
    load();
    // Com a janela de atendimento aberta, confere a fila mais seguido pra avisar rapido.
    const t = setInterval(load, hasTabs ? 5000 : 15000);
    return () => clearInterval(t);
  }, [load, hasTabs]);

  // Abas em segundo plano nao leem a conversa, e a pausa so se mantem enquanto alguem
  // le: um "toque" leve a cada 60 s evita o bot voltar a responder numa aba parada.
  useEffect(() => {
    const background = tabs.filter((ph) => ph !== openChat);
    if (!background.length) return undefined;
    const t = setInterval(() => {
      background.forEach((ph) => api.get(`/chat-sessions/${ph}/thread`, { params: { limit: 1 } }).catch(() => {}));
    }, 60000);
    return () => clearInterval(t);
  }, [tabs, openChat]);

  const act = async (phone, action) => {
    setBusy(phone + action);
    try {
      const { data: res } = await api.post(`/chat-sessions/${phone}/${action}`);
      toast.success(action === "end-pause" ? `Atendimento de ${phone} encerrado` : `Pausa estendida até ${new Date(res.paused_until).toLocaleTimeString("pt-BR", tzOpts())}`);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(null);
    }
  };

  const openConversation = data.items.find((s) => s.phone === openChat);

  // Conversas que pedem atendimento (ou o cliente respondeu) e ainda nao estao nas abas.
  const incoming = data.items.filter((s) => (s.waiting || s.needs_reply) && !tabs.includes(s.phone) && !dismissed[s.phone]);
  // "Depois" vale ate a conversa sair da fila (quando voltar a pedir, avisa de novo).
  useEffect(() => {
    setDismissed((cur) => {
      const keep = {};
      Object.keys(cur).forEach((ph) => {
        const it = data.items.find((s) => s.phone === ph);
        if (it && (it.waiting || it.needs_reply)) keep[ph] = true;
      });
      return Object.keys(keep).length === Object.keys(cur).length ? cur : keep;
    });
  }, [data.items]);
  // Alerta sonoro + titulo piscando quando entra atendimento novo (ou o cliente responde).
  // A primeira carga da pagina nao toca (so registra o que ja estava na fila).
  useEffect(() => {
    const keys = new Set(
      data.items.filter((s) => s.waiting || s.needs_reply || s.mine)
        .map((s) => `${s.phone}|${s.waiting ? "w" : "r"}|${s.paused_at || ""}|${s.client_replied_at || ""}|${s.mine ? s.assigned_at : ""}`),
    );
    if (alertedRef.current === null) { alertedRef.current = keys; return; }
    let fresh = false;
    keys.forEach((k) => { if (!alertedRef.current.has(k)) fresh = true; });
    alertedRef.current = keys;
    if (fresh) {
      if (soundEnabled()) playBeep();
      setBlink(true);
    }
  }, [data.items]);
  useEffect(() => { if (data.items.every((s) => !(s.waiting || s.needs_reply))) setBlink(false); }, [data.items]);
  useTitleBlink(blink, "🔔 Novo atendimento");

  // Aba ativa conta como "vista" ate a ultima mensagem do cliente que chegou.
  useEffect(() => {
    if (!openChat) return;
    const it = data.items.find((s) => s.phone === openChat);
    if (it?.last_in_at) setSeen((cur) => (cur[openChat] === it.last_in_at ? cur : { ...cur, [openChat]: it.last_in_at }));
  }, [openChat, data.items]);

  const tabInfo = tabs.map((ph) => {
    const it = data.items.find((s) => s.phone === ph);
    const unread = !!(it?.last_in_at && ph !== openChat && String(it.last_in_at) > String(seen[ph] || ""));
    return { phone: ph, label: it?.client_name || ph, unread };
  });

  // Fila: quem espera ha mais tempo primeiro, depois quem respondeu, depois o resto.
  const rank = (s) => (s.waiting ? 0 : s.needs_reply ? 1 : 2);
  const sortedItems = [...data.items].sort((a, b) =>
    rank(a) - rank(b) || (b.wait_seconds || 0) - (a.wait_seconds || 0));
  const WAIT_ALERT_S = 5 * 60;
  const viewingOpen = data.items.find((s) => s.phone === openChat)?.viewing_by || null;
  const assignedOpen = data.items.find((s) => s.phone === openChat) || null;

  return (
    <Card className="bg-slate-900/50 border-slate-800 p-6" data-testid="paused-sessions-panel">
      <div className="flex items-center justify-between border-b border-slate-800 pb-3">
        <div className="flex items-center gap-2">
          <PauseCircle className="w-4 h-4 text-amber-400" />
          <div className="font-outfit text-white font-semibold">Conversas pausadas (atendimento humano)</div>
          <Badge className="bg-amber-500/10 text-amber-300 border border-amber-500/30" data-testid="paused-count">
            {data.items.length}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" data-testid="sound-toggle"
            title={sound ? "Som de novo atendimento ligado" : "Som de novo atendimento desligado"}
            onClick={() => { const v = !sound; setSound(v); setSoundEnabled(v); if (v) playBeep(); }}
            className="text-slate-400 hover:text-white">
            {sound ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
          </Button>
          <Button size="sm" onClick={() => setNewOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950" data-testid="new-conversation-btn">
            <MessageCirclePlus className="w-4 h-4 mr-1" /> Novo atendimento
          </Button>
          <Button size="sm" variant="ghost" onClick={load} className="text-slate-400 hover:text-white" data-testid="paused-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      {data.whatsapp_alert && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-100" data-testid="whatsapp-alert">
          <WifiOffIcon className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>WhatsApp fora do ar</b> desde {new Date(data.whatsapp_alert.since).toLocaleTimeString("pt-BR", tzOpts())}: os envios estão falhando
            (“Connection Closed”). O Quitaí tenta reiniciar a conexão sozinho a cada 10 minutos. Se continuar, reconecte em
            WhatsApp → Reconectar (pode pedir o QR code).
          </div>
        </div>
      )}
      <p className="text-xs text-slate-500 mt-3">
        Enquanto pausada, o bot não responde ao cliente. Clique em "Ver conversa" para responder direto pelo painel — a pausa
        se mantém ativa sozinha enquanto a janela estiver aberta. "+{data.pause_minutes} min" estende sem abrir a conversa.
      </p>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
            <tr>
              <th className="text-left py-2 px-2">Número</th>
              <th className="text-left py-2 px-2">Cliente</th>
              <th className="text-left py-2 px-2">Motivo</th>
              <th className="text-left py-2 px-2">Termina em</th>
              <th className="text-right py-2 px-2"></th>
            </tr>
          </thead>
          <tbody data-testid="paused-tbody">
            {data.items.length === 0 && (
              <tr>
                <td colSpan={5} className="py-5 text-center text-slate-500">Nenhuma conversa pausada no momento.</td>
              </tr>
            )}
            {sortedItems.map((s) => (
              <tr key={s.phone} data-testid={`paused-row-${s.phone}`} className="border-b border-slate-800/60">
                <td className="py-2 px-2 font-mono-jb text-slate-300">{s.phone}</td>
                <td className="py-2 px-2 text-white">
                  {s.client_name || <span className="text-slate-600">não identificado</span>}
                  {s.client_login && <span className="ml-2 font-mono-jb text-xs text-emerald-300">{s.client_login}</span>}
                  {(s.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {s.tags.map((tg) => (
                        <span key={tg} className="text-[10px] rounded-full border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-slate-300">{tg}</span>
                      ))}
                    </div>
                  )}
                  {s.notes_count > 0 && (
                    <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-amber-300" title="Notas internas">
                      <StickyNote className="w-3 h-3" /> {s.notes_count}
                    </span>
                  )}
                  {s.assigned_to && (
                    <div className={`mt-1 text-[10px] ${s.mine ? "text-emerald-300" : "text-slate-400"}`} data-testid={`assigned-${s.phone}`}>
                      Responsável: {s.mine ? "você" : (s.assigned_name || s.assigned_to)}
                    </div>
                  )}
                  {s.viewing_by && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-300" data-testid={`viewing-${s.phone}`}>
                      <Eye className="w-3 h-3" /> {s.viewing_by.split("@")[0]} está com a conversa
                    </div>
                  )}
                </td>
                <td className="py-2 px-2 text-slate-400 text-xs">
                  {REASON[s.pause_reason] || s.pause_reason}
                  {s.waiting && s.wait_seconds != null && (
                    <Badge data-testid={`wait-${s.phone}`}
                      className={`ml-2 border ${s.wait_seconds >= WAIT_ALERT_S
                        ? "bg-red-500/15 text-red-300 border-red-500/40 animate-pulse"
                        : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
                      aguardando há {fmtWait(s.wait_seconds)}
                    </Badge>
                  )}
                  {s.outbound && s.needs_reply && <Badge className="ml-2 bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">Cliente respondeu</Badge>}
                  {s.outbound && !s.client_replied_at && <span className="ml-2 text-slate-500">aguardando cliente</span>}
                </td>
                <td className="py-2 px-2 font-mono-jb text-amber-300">
                  {fmtRemaining(s.remaining_seconds)}
                  <span className="text-slate-600 text-xs ml-2">({new Date(s.paused_until).toLocaleTimeString("pt-BR", tzOpts())})</span>
                </td>
                <td className="py-2 px-2 text-right whitespace-nowrap">
                  <Button
                    size="sm" disabled={!!busy}
                    data-testid={`open-chat-btn-${s.phone}`}
                    onClick={() => openTab(s.phone)}
                    className="bg-emerald-500/90 hover:bg-emerald-400 text-emerald-950 text-xs mr-2"
                  >
                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> Ver conversa
                  </Button>
                  <Button
                    size="sm" variant="outline" disabled={!!busy}
                    data-testid={`extend-pause-btn-${s.phone}`}
                    onClick={() => act(s.phone, "extend-pause")}
                    className="border-slate-700 hover:bg-slate-800 hover:text-white text-xs mr-2"
                  >
                    <TimerReset className="w-3.5 h-3.5 mr-1" /> +{data.pause_minutes} min
                  </Button>
                  <Button
                    size="sm" disabled={!!busy}
                    data-testid={`end-pause-btn-${s.phone}`}
                    onClick={() => act(s.phone, "end-pause")}
                    className="bg-red-500/80 hover:bg-red-400 text-white text-xs"
                  >
                    <StopCircle className="w-3.5 h-3.5 mr-1" /> Encerrar agora
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewConversationDialog open={newOpen} onClose={() => setNewOpen(false)}
        onStarted={(ph) => { load(); openTab(ph); }} />

      <ConversationDialog
        phone={openChat}
        clientName={openConversation?.client_name}
        open={!!openChat}
        onClose={closeAll}
        onSent={load}
        tabs={tabInfo}
        onSelectTab={setOpenChat}
        onCloseTab={closeTab}
        incoming={incoming}
        viewingBy={viewingOpen}
        assigned={assignedOpen}
        onMeta={load}
        onPull={(ph) => openTab(ph)}
        onDismiss={(ph) => setDismissed((cur) => ({ ...cur, [ph]: true }))}
      />
    </Card>
  );
}
